// Daily trends from tracker entries, in the family's time zone.
// Sleep: night sleep belongs to the night that STARTED on day D (even past midnight);
// naps belong to their local start date; `calendarSleepMin` is sleep split exactly at local midnight.

import type { DiaperKind, Entry } from './entries.js';
import { pumpTotalMl } from './entries.js';
import { mean } from './math.js';
import { MAX_FEED_MIN } from './sanity.js';
import {
  buildSleepDays,
  classifySleeps,
  defaultClassifyOptions,
  durationMin,
  nightWakings,
  normalizeSleeps,
  type ClassifiedSegment,
} from './sleep-sessions.js';
import {
  addDays,
  diffDays,
  formatHHmm,
  localDate,
  localMinutesOfDay,
  MS_PER_MINUTE,
  startOfLocalDay,
  toIso,
  toMs,
  type Instant,
} from './tz.js';

export interface DailySleepStats {
  hasData: boolean;
  /** daySleepMin + nightSleepMin (the "sleep day": naps of D + the night starting on D). */
  totalMin: number;
  daySleepMin: number;
  nightSleepMin: number;
  /** Sleep inside local calendar day D (00:00–24:00), split at midnight. */
  calendarSleepMin: number;
  napCount: number;
  longestStretchMin: number;
  nightWakings: number;
  nightAwakeMin: number;
  /** Morning wake on D (end of the previous night). */
  wakeUpAt: string | null;
  wakeUpTime: string | null; // local 'HH:mm'
  bedtimeAt: string | null;
  bedtime: string | null; // local 'HH:mm'
  /** The night of D is still in progress (counted up to now). */
  nightOngoing: boolean;
}

export interface DailyFeedingStats {
  feedsCount: number; // breast + bottle
  breastCount: number;
  bottleCount: number;
  bottleMl: number;
  breastMin: { left: number; right: number; unknownSide: number; total: number };
  pumpMl: number;
  solidsCount: number;
}

export type DiaperCounts = Record<DiaperKind, number> & { total: number };

export interface DayStats {
  date: string;
  sleep: DailySleepStats;
  feeding: DailyFeedingStats;
  diapers: DiaperCounts;
  /** Count of entries of any type that start on this date. */
  entryCount: number;
}

export interface StatsInput {
  entries: readonly Entry[];
  tz: string;
  /** Inclusive local dates. */
  from: string;
  to: string;
  now: Instant;
  /** Corrected age (ageForSleepDays): tunes the nap/night boundary. */
  ageDays?: number | undefined;
  settings?: { targetBedtime?: string | undefined } | undefined;
}

const MIN = MS_PER_MINUTE;
const r1 = (n: number): number => Math.round(n * 10) / 10;

/** Drop exact duplicates (same id, or same type/start/data). */
export function dedupeEntries<T extends Entry>(entries: readonly T[]): T[] {
  const ids = new Set<string>();
  const keys = new Set<string>();
  const out: T[] = [];
  for (const e of entries) {
    if (ids.has(e.id)) continue;
    const key = `${e.type}|${toMs(e.startAt)}|${e.endAt === null ? '' : toMs(e.endAt)}|${JSON.stringify(e.data)}`;
    if (keys.has(key)) continue;
    ids.add(e.id);
    keys.add(key);
    out.push(e);
  }
  return out;
}

function overlapMin(a: { start: number; end: number }, from: number, to: number): number {
  return Math.max(0, Math.min(a.end, to) - Math.max(a.start, from)) / MIN;
}

function emptyDay(date: string): DayStats {
  return {
    date,
    sleep: {
      hasData: false,
      totalMin: 0,
      daySleepMin: 0,
      nightSleepMin: 0,
      calendarSleepMin: 0,
      napCount: 0,
      longestStretchMin: 0,
      nightWakings: 0,
      nightAwakeMin: 0,
      wakeUpAt: null,
      wakeUpTime: null,
      bedtimeAt: null,
      bedtime: null,
      nightOngoing: false,
    },
    feeding: {
      feedsCount: 0,
      breastCount: 0,
      bottleCount: 0,
      bottleMl: 0,
      breastMin: { left: 0, right: 0, unknownSide: 0, total: 0 },
      pumpMl: 0,
      solidsCount: 0,
    },
    diapers: { wet: 0, dirty: 0, mixed: 0, dry: 0, total: 0 },
    entryCount: 0,
  };
}

/** Per-day stats for every local date in [from, to]. */
export function computeDailyStats(input: StatsInput): DayStats[] {
  const { tz } = input;
  const nowMs = toMs(input.now);
  const n = diffDays(input.from, input.to);
  if (n < 0) return [];
  const result = new Map<string, DayStats>();
  for (let i = 0; i <= n; i++) {
    const d = addDays(input.from, i);
    result.set(d, emptyDay(d));
  }
  const entries = dedupeEntries(input.entries);

  // --- Sleep (window padded by a day each side to catch nights crossing the range edges)
  const padFrom = startOfLocalDay(addDays(input.from, -1), tz);
  const padTo = startOfLocalDay(addDays(input.to, 2), tz);
  const sleeps = entries.filter(
    (e) => e.type === 'sleep' && toMs(e.startAt) < padTo && (e.endAt === null || toMs(e.endAt) > padFrom),
  );
  const segs = classifySleeps(normalizeSleeps(sleeps, nowMs), defaultClassifyOptions(tz, input.ageDays, input.settings));
  const sleepDays = buildSleepDays(segs, tz);

  for (const day of result.values()) {
    const s = day.sleep;
    const sd = sleepDays.get(day.date);
    const dayStart = startOfLocalDay(day.date, tz);
    const dayEnd = startOfLocalDay(addDays(day.date, 1), tz);
    s.calendarSleepMin = r1(segs.reduce((acc, seg) => acc + overlapMin(seg, dayStart, dayEnd), 0));
    if (!sd) {
      s.hasData = s.calendarSleepMin > 0;
      continue;
    }
    const naps: ClassifiedSegment[] = sd.naps;
    s.napCount = naps.length;
    s.daySleepMin = r1(naps.reduce((a, x) => a + durationMin(x), 0));
    s.nightSleepMin = r1(sd.night.reduce((a, x) => a + durationMin(x), 0));
    s.totalMin = r1(s.daySleepMin + s.nightSleepMin);
    s.longestStretchMin = r1(Math.max(0, ...naps.map(durationMin), ...sd.night.map(durationMin)));
    const w = nightWakings(sd.night);
    s.nightWakings = w.count;
    s.nightAwakeMin = r1(w.awakeMin);
    s.nightOngoing = sd.night.some((x) => x.ongoing);
    if (sd.morningWake !== null) {
      s.wakeUpAt = toIso(sd.morningWake);
      s.wakeUpTime = formatHHmm(localMinutesOfDay(sd.morningWake, tz));
    }
    if (sd.bedtime !== null) {
      s.bedtimeAt = toIso(sd.bedtime);
      s.bedtime = formatHHmm(localMinutesOfDay(sd.bedtime, tz));
    }
    s.hasData = naps.length > 0 || sd.night.length > 0 || sd.morningWake !== null || s.calendarSleepMin > 0;
  }

  // --- Other entries: attributed to the local date of startAt.
  for (const e of entries) {
    const startMs = toMs(e.startAt);
    if (startMs > nowMs) continue;
    const day = result.get(localDate(startMs, tz));
    if (!day) continue;
    day.entryCount++;
    const f = day.feeding;
    switch (e.type) {
      case 'breast': {
        f.breastCount++;
        f.feedsCount++;
        let segTotal = 0;
        for (const seg of e.data.segments) {
          const a = toMs(seg.startAt);
          const b = seg.endAt === null ? nowMs : Math.min(toMs(seg.endAt), nowMs);
          const m = Math.max(0, (b - a) / MIN);
          f.breastMin[seg.side] += m;
          segTotal += m;
        }
        if (e.data.segments.length === 0) {
          const end = e.endAt === null ? nowMs : Math.min(toMs(e.endAt), nowMs);
          const m = Math.min(MAX_FEED_MIN, Math.max(0, (end - startMs) / MIN));
          if (e.data.lastSide) f.breastMin[e.data.lastSide] += m;
          else f.breastMin.unknownSide += m;
          segTotal = m;
        } else if (segTotal > MAX_FEED_MIN) {
          // a forgotten timer: count at most MAX_FEED_MIN, shared between the sides as logged
          const k = MAX_FEED_MIN / segTotal;
          for (const seg of e.data.segments) {
            const a = toMs(seg.startAt);
            const b = seg.endAt === null ? nowMs : Math.min(toMs(seg.endAt), nowMs);
            f.breastMin[seg.side] -= Math.max(0, (b - a) / MIN) * (1 - k);
          }
          segTotal = MAX_FEED_MIN;
        }
        f.breastMin.total += segTotal;
        break;
      }
      case 'bottle':
        f.bottleCount++;
        f.feedsCount++;
        f.bottleMl += e.data.amountMl;
        break;
      case 'pump':
        f.pumpMl += pumpTotalMl(e.data);
        break;
      case 'solids':
        f.solidsCount++;
        break;
      case 'diaper':
        day.diapers[e.data.kind]++;
        day.diapers.total++;
        break;
      default:
        break;
    }
  }
  for (const day of result.values()) {
    const b = day.feeding.breastMin;
    b.left = r1(b.left);
    b.right = r1(b.right);
    b.unknownSide = r1(b.unknownSide);
    b.total = r1(b.total);
  }
  return [...result.values()];
}

export interface StatsAverages {
  totalSleepMin: number | null;
  daySleepMin: number | null;
  nightSleepMin: number | null;
  napCount: number | null;
  longestStretchMin: number | null;
  nightWakings: number | null;
  nightAwakeMin: number | null;
  /** Average local wake-up / bedtime, minutes of day (bedtime may exceed 1440 for after-midnight). */
  wakeUpMin: number | null;
  bedtimeMin: number | null;
  wakeUpTime: string | null;
  bedtime: string | null;
  feedsCount: number | null;
  bottleMl: number | null;
  breastMin: number | null;
  diapers: number | null;
  wetDiapers: number | null;
  dirtyDiapers: number | null;
}

export interface StatsSummary {
  from: string;
  to: string;
  days: number;
  daysWithSleep: number;
  daysWithFeeding: number;
  averages: StatsAverages;
}

const avg = (xs: number[]): number | null => {
  const m = mean(xs);
  return m === null ? null : r1(m);
};

/**
 * Averages over the given days. Sleep metrics average over days with complete sleep data
 * (night not in progress); feeding/diaper metrics over days that have any such entry.
 */
export function summarizeDays(days: readonly DayStats[], tz?: string): StatsSummary {
  const sleepDays = days.filter((d) => d.sleep.hasData && !d.sleep.nightOngoing);
  const nightDays = sleepDays.filter((d) => d.sleep.bedtimeAt !== null);
  const feedDays = days.filter((d) => d.feeding.feedsCount > 0);
  const diaperDays = days.filter((d) => d.diapers.total > 0);
  const wake = tz
    ? days.flatMap((d) => (d.sleep.wakeUpAt ? [localMinutesOfDay(d.sleep.wakeUpAt, tz)] : []))
    : days.flatMap((d) => (d.sleep.wakeUpTime ? [hhmmToMin(d.sleep.wakeUpTime)] : []));
  const bed = days.flatMap((d) => {
    if (!d.sleep.bedtime) return [];
    const m = hhmmToMin(d.sleep.bedtime);
    return [m < 12 * 60 ? m + 1440 : m]; // after-midnight bedtimes stay "late"
  });
  const wakeAvg = avg(wake);
  const bedAvg = avg(bed);
  return {
    from: days[0]?.date ?? '',
    to: days[days.length - 1]?.date ?? '',
    days: days.length,
    daysWithSleep: sleepDays.length,
    daysWithFeeding: feedDays.length,
    averages: {
      totalSleepMin: avg(nightDays.map((d) => d.sleep.totalMin)),
      daySleepMin: avg(sleepDays.map((d) => d.sleep.daySleepMin)),
      nightSleepMin: avg(nightDays.map((d) => d.sleep.nightSleepMin)),
      napCount: avg(sleepDays.map((d) => d.sleep.napCount)),
      longestStretchMin: avg(sleepDays.map((d) => d.sleep.longestStretchMin)),
      nightWakings: avg(nightDays.map((d) => d.sleep.nightWakings)),
      nightAwakeMin: avg(nightDays.map((d) => d.sleep.nightAwakeMin)),
      wakeUpMin: wakeAvg,
      bedtimeMin: bedAvg,
      wakeUpTime: wakeAvg === null ? null : formatHHmm(wakeAvg),
      bedtime: bedAvg === null ? null : formatHHmm(bedAvg),
      feedsCount: avg(feedDays.map((d) => d.feeding.feedsCount)),
      bottleMl: avg(feedDays.map((d) => d.feeding.bottleMl)),
      breastMin: avg(feedDays.map((d) => d.feeding.breastMin.total)),
      diapers: avg(diaperDays.map((d) => d.diapers.total)),
      wetDiapers: avg(diaperDays.map((d) => d.diapers.wet + d.diapers.mixed)),
      dirtyDiapers: avg(diaperDays.map((d) => d.diapers.dirty + d.diapers.mixed)),
    },
  };
}

function hhmmToMin(s: string): number {
  const [h, m] = s.split(':');
  return Number(h) * 60 + Number(m);
}

export interface TrendsInput extends Omit<StatsInput, 'from' | 'to'> {
  /** Period length, e.g. 7, 14 or 30. */
  periodDays: number;
  /** Include today's partial day in the current period (default false: ends yesterday). */
  includeToday?: boolean;
}

export interface Trends {
  current: StatsSummary;
  previous: StatsSummary;
  /** current − previous (abs) and relative change in % (null if previous is null/0). */
  delta: Partial<Record<keyof StatsAverages, { abs: number | null; pct: number | null }>>;
  days: DayStats[];
}

const NUMERIC_KEYS: (keyof StatsAverages)[] = [
  'totalSleepMin',
  'daySleepMin',
  'nightSleepMin',
  'napCount',
  'longestStretchMin',
  'nightWakings',
  'nightAwakeMin',
  'wakeUpMin',
  'bedtimeMin',
  'feedsCount',
  'bottleMl',
  'breastMin',
  'diapers',
  'wetDiapers',
  'dirtyDiapers',
];

/** Current period vs the previous period of the same length. */
export function computeTrends(input: TrendsInput): Trends {
  const today = localDate(input.now, input.tz);
  const to = input.includeToday ? today : addDays(today, -1);
  const from = addDays(to, -(input.periodDays - 1));
  const prevTo = addDays(from, -1);
  const prevFrom = addDays(prevTo, -(input.periodDays - 1));
  const all = computeDailyStats({ ...input, from: prevFrom, to });
  const prevDays = all.slice(0, input.periodDays);
  const curDays = all.slice(input.periodDays);
  const current = summarizeDays(curDays, input.tz);
  const previous = summarizeDays(prevDays, input.tz);
  const delta: Trends['delta'] = {};
  for (const k of NUMERIC_KEYS) {
    const c = current.averages[k];
    const p = previous.averages[k];
    if (typeof c === 'string' || typeof p === 'string') continue;
    delta[k] = {
      abs: c === null || p === null ? null : r1(c - p),
      pct: c === null || p === null || p === 0 ? null : r1(((c - p) / p) * 100),
    };
  }
  return { current, previous, delta, days: curDays };
}

/** Convenience: total sleep in a raw time window (merged, ongoing counted to now). */
export function sleepMinutesBetween(entries: readonly Entry[], from: Instant, to: Instant, now: Instant): number {
  const a = toMs(from);
  const b = toMs(to);
  const segs = normalizeSleeps(
    entries.filter((e) => e.type === 'sleep'),
    now,
  );
  return r1(segs.reduce((acc, s) => acc + overlapMin(s, a, b), 0));
}
